import React, { useState, useCallback } from "react";

const COLORS = {
  bg: "#0f1117",
  panel: "#161b27",
  border: "#1e2535",
  accent: "#4f8ef7",
  added: "#22c55e",
  removed: "#ef4444",
  changed: "#f59e0b",
  unchanged: "#64748b",
  text: "#e2e8f0",
  muted: "#475569",
  addedBg: "rgba(34,197,94,0.08)",
  removedBg: "rgba(239,68,68,0.08)",
  changedBg: "rgba(245,158,11,0.08)",
};

function deepDiff(a, b, path = "") {
  const diffs = [];

  if (typeof a !== typeof b || Array.isArray(a) !== Array.isArray(b)) {
    diffs.push({ path, type: "changed", left: a, right: b });
    return diffs;
  }

  if (typeof a !== "object" || a === null) {
    if (a !== b) diffs.push({ path, type: "changed", left: a, right: b });
    return diffs;
  }

  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of allKeys) {
    const newPath = path ? `${path}.${key}` : key;
    if (!(key in a)) {
      diffs.push({ path: newPath, type: "added", left: undefined, right: b[key] });
    } else if (!(key in b)) {
      diffs.push({ path: newPath, type: "removed", left: a[key], right: undefined });
    } else {
      diffs.push(...deepDiff(a[key], b[key], newPath));
    }
  }
  return diffs;
}

function formatValue(val) {
  if (val === undefined) return "—";
  if (val === null) return "null";
  if (typeof val === "object") return JSON.stringify(val, null, 2);
  return String(val);
}

function Badge({ type }) {
  const map = {
    added: { label: "ADDED", color: COLORS.added },
    removed: { label: "REMOVED", color: COLORS.removed },
    changed: { label: "CHANGED", color: COLORS.changed },
  };
  const { label, color } = map[type] || {};
  return (
    <span style={{
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "0.1em",
      color,
      border: `1px solid ${color}`,
      borderRadius: "3px",
      padding: "1px 6px",
      fontFamily: "monospace",
    }}>{label}</span>
  );
}

function DiffRow({ diff }) {
  const bgMap = { added: COLORS.addedBg, removed: COLORS.removedBg, changed: COLORS.changedBg };
  const colorMap = { added: COLORS.added, removed: COLORS.removed, changed: COLORS.changed };
  return (
    <div style={{
      background: bgMap[diff.type],
      borderLeft: `3px solid ${colorMap[diff.type]}`,
      borderRadius: "6px",
      padding: "12px 16px",
      marginBottom: "8px",
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <Badge type={diff.type} />
        <span style={{ color: COLORS.accent, fontSize: "13px", fontWeight: 600 }}>{diff.path}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        {diff.type !== "added" && (
          <div>
            <div style={{ fontSize: "10px", color: COLORS.removed, letterSpacing: "0.08em", marginBottom: "4px" }}>◀ DOC 1</div>
            <pre style={{
              margin: 0, fontSize: "12px", color: COLORS.removed,
              background: "rgba(239,68,68,0.06)", borderRadius: "4px",
              padding: "6px 10px", whiteSpace: "pre-wrap", wordBreak: "break-all",
              maxHeight: "120px", overflow: "auto",
            }}>{formatValue(diff.left)}</pre>
          </div>
        )}
        {diff.type !== "removed" && (
          <div style={diff.type === "added" ? { gridColumn: "1 / -1" } : {}}>
            <div style={{ fontSize: "10px", color: COLORS.added, letterSpacing: "0.08em", marginBottom: "4px" }}>▶ DOC 2</div>
            <pre style={{
              margin: 0, fontSize: "12px", color: COLORS.added,
              background: "rgba(34,197,94,0.06)", borderRadius: "4px",
              padding: "6px 10px", whiteSpace: "pre-wrap", wordBreak: "break-all",
              maxHeight: "120px", overflow: "auto",
            }}>{formatValue(diff.right)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

function Summary({ diffs }) {
  const added = diffs.filter(d => d.type === "added").length;
  const removed = diffs.filter(d => d.type === "removed").length;
  const changed = diffs.filter(d => d.type === "changed").length;
  const total = diffs.length;

  if (total === 0) {
    return (
      <div style={{
        textAlign: "center", padding: "40px",
        background: "rgba(34,197,94,0.08)",
        borderRadius: "10px", border: `1px solid rgba(34,197,94,0.3)`,
      }}>
        <div style={{ fontSize: "40px", marginBottom: "12px" }}>✓</div>
        <div style={{ color: COLORS.added, fontSize: "18px", fontWeight: 700 }}>Documents are identical</div>
        <div style={{ color: COLORS.muted, fontSize: "13px", marginTop: "6px" }}>No differences found</div>
      </div>
    );
  }

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
      gap: "12px", marginBottom: "20px",
    }}>
      {[
        { label: "Added", count: added, color: COLORS.added },
        { label: "Removed", count: removed, color: COLORS.removed },
        { label: "Changed", count: changed, color: COLORS.changed },
      ].map(({ label, count, color }) => (
        <div key={label} style={{
          background: COLORS.panel, borderRadius: "8px",
          border: `1px solid ${COLORS.border}`, padding: "14px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: "28px", fontWeight: 800, color, fontFamily: "monospace" }}>{count}</div>
          <div style={{ fontSize: "12px", color: COLORS.muted, letterSpacing: "0.08em", marginTop: "2px" }}>{label.toUpperCase()}</div>
        </div>
      ))}
    </div>
  );
}

export default function JSONDiffTool() {
  const [doc1, setDoc1] = useState("");
  const [doc2, setDoc2] = useState("");
  const [diffs, setDiffs] = useState(null);
  const [errors, setErrors] = useState({ doc1: "", doc2: "" });
  const [filter, setFilter] = useState("all");

  const compare = useCallback(() => {
    const errs = { doc1: "", doc2: "" };
    let parsed1, parsed2;
    try { parsed1 = JSON.parse(doc1); } catch (e) { errs.doc1 = "Invalid JSON: " + e.message; }
    try { parsed2 = JSON.parse(doc2); } catch (e) { errs.doc2 = "Invalid JSON: " + e.message; }
    setErrors(errs);
    if (errs.doc1 || errs.doc2) return;
    setDiffs(deepDiff(parsed1, parsed2));
    setFilter("all");
  }, [doc1, doc2]);

  const clear = () => { setDoc1(""); setDoc2(""); setDiffs(null); setErrors({ doc1: "", doc2: "" }); };

  const filteredDiffs = diffs ? (filter === "all" ? diffs : diffs.filter(d => d.type === filter)) : [];

  const inputStyle = (err) => ({
    width: "100%", minHeight: "220px", resize: "vertical",
    background: "#0a0d14", border: `1px solid ${err ? COLORS.removed : COLORS.border}`,
    borderRadius: "8px", color: COLORS.text, padding: "12px",
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    fontSize: "12px", lineHeight: "1.6", outline: "none",
    boxSizing: "border-box", transition: "border-color 0.2s",
  });

  return (
    <div style={{
      minHeight: "100vh", background: COLORS.bg, color: COLORS.text,
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif", padding: "32px 24px",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');
        * { box-sizing: border-box; }
        textarea:focus { border-color: #4f8ef7 !important; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #2a3347; border-radius: 3px; }
        button:hover { opacity: 0.88; transform: translateY(-1px); }
        button { transition: all 0.15s ease; }
      `}</style>

      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "8px",
              background: `linear-gradient(135deg, ${COLORS.accent}, #7c3aed)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "18px",
            }}>⟷</div>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: 700, letterSpacing: "-0.02em" }}>
              JSON Diff
            </h1>
          </div>
          <p style={{ margin: 0, color: COLORS.muted, fontSize: "14px" }}>
            Paste two JSON documents to compare values, structure, and fields
          </p>
        </div>

        {/* Input panels */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
          {[
            { label: "Document 1", val: doc1, set: setDoc1, err: errors.doc1 },
            { label: "Document 2", val: doc2, set: setDoc2, err: errors.doc2 },
          ].map(({ label, val, set, err }) => (
            <div key={label}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: "8px",
              }}>
                <label style={{ fontSize: "13px", fontWeight: 600, color: COLORS.muted, letterSpacing: "0.05em" }}>
                  {label.toUpperCase()}
                </label>
                {err && <span style={{ fontSize: "11px", color: COLORS.removed }}>{err}</span>}
              </div>
              <textarea
                value={val}
                onChange={e => set(e.target.value)}
                placeholder={`Paste ${label.toLowerCase()} here...`}
                style={inputStyle(err)}
              />
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "32px" }}>
          <button
            onClick={compare}
            style={{
              background: `linear-gradient(135deg, ${COLORS.accent}, #7c3aed)`,
              color: "#fff", border: "none", borderRadius: "8px",
              padding: "11px 28px", fontSize: "14px", fontWeight: 600,
              cursor: "pointer", letterSpacing: "0.02em",
            }}
          >
            Compare →
          </button>
          <button
            onClick={clear}
            style={{
              background: "transparent", color: COLORS.muted,
              border: `1px solid ${COLORS.border}`, borderRadius: "8px",
              padding: "11px 20px", fontSize: "14px", cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>

        {/* Results */}
        {diffs !== null && (
          <div>
            <Summary diffs={diffs} />

            {diffs.length > 0 && (
              <>
                {/* Filter tabs */}
                <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                  {[
                    { key: "all", label: `All (${diffs.length})` },
                    { key: "changed", label: `Changed (${diffs.filter(d => d.type === "changed").length})` },
                    { key: "added", label: `Added (${diffs.filter(d => d.type === "added").length})` },
                    { key: "removed", label: `Removed (${diffs.filter(d => d.type === "removed").length})` },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setFilter(key)}
                      style={{
                        background: filter === key ? COLORS.accent : "transparent",
                        color: filter === key ? "#fff" : COLORS.muted,
                        border: `1px solid ${filter === key ? COLORS.accent : COLORS.border}`,
                        borderRadius: "6px", padding: "6px 14px",
                        fontSize: "12px", fontWeight: 600, cursor: "pointer",
                        letterSpacing: "0.03em",
                      }}
                    >{label}</button>
                  ))}
                </div>

                {/* Diff list */}
                <div>
                  {filteredDiffs.length === 0
                    ? <div style={{ color: COLORS.muted, textAlign: "center", padding: "20px", fontSize: "14px" }}>No differences of this type</div>
                    : filteredDiffs.map((diff, i) => <DiffRow key={i} diff={diff} />)
                  }
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}